import { loadWrapper } from "aic";
import { ActiveNavLink } from "components/common/ActiveNavLink";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { useContext, useEffect, useEffectEvent, useState } from "react";
import { Spinner } from "react-bootstrap";
import { eHierarchyLevel } from "../../functions/api/enums";
import {
  HierarchySearchDto,
  searchAllHierarchies,
} from "../../functions/api/hierarchy/searchAllHierarchies";
import { IRequestStatus } from "../../functions/api/interface";

/**
 * Displays search results for hierarchies. Runs a backend search and renders
 * links to matching hierarchy pages. Supports a read-only mode when
 * `updateable` is false.
 *
 * @param props - Destructured props object.
 * @param props.searchText - The query text used to search hierarchies.
 * @param props.updateable - If true the results are considered updateable.
 */
export const SearchResultsHierarchy = ({
  searchText,
  updateable = false,
}: {
  searchText: string;
  updateable?: boolean;
}): React.ReactElement => {
  const appSettings = useContext(AppSettingsContext);
  const [searchStatus, setSearchStatus] = useState<IRequestStatus<string>>({
    requesting: false,
    error: false,
  });
  const [searchResults, setSearchResults] = useState<HierarchySearchDto[]>([]);

  // Run search
  const search = useEffectEvent(async (searchText: string) => {
    const response = await loadWrapper(
      searchAllHierarchies,
      searchText,
      { ...searchStatus, error: false },
      setSearchStatus,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (response?.success) {
      setSearchResults(response.data);
    } else if (response && response.success === false) {
      setSearchResults([]);
    }
  });

  useEffect(() => {
    search(searchText);
  }, [searchText]);

  return (
    <>
      {searchStatus.requesting ? (
        <div className="my-2">
          Loading search results for Hierarchies...{" "}
          <Spinner
            animation="border"
            size="sm"
            variant="dark"
          />
        </div>
      ) : searchStatus.error ? (
        <p className="text-danger">{searchStatus.errorText}</p>
      ) : !updateable && searchResults.length === 0 ? (
        <p>No results for Hierarchies</p>
      ) : (
        <div className="mb-2">
          <h5
            className="mt-1"
            style={{ fontSize: "24px" }}
          >
            Hierarchies
          </h5>
          {searchResults.map((h, hi) => (
            <div key={hi}>
              <ActiveNavLink to={`/H/${h.id}`}>
                {h.level === eHierarchyLevel.Report ? `${h.parentTag}/${h.name}` : h.name}
              </ActiveNavLink>
              {" - "}
              {!h.description || h.description === "" ? <em>No description</em> : h.description}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

SearchResultsHierarchy.displayName = "SearchResultsHierarchy";
